import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    IconSearch,
    IconFilter,
    IconRefresh,
    IconChevronUp,
    IconChevronDown,
    IconPencil,
    IconTrash,
    IconX,
    IconBan
} from "@tabler/icons-react";

// Redux
import { useSelector, useDispatch } from "react-redux";
import {
    fetchProductsList,
    searchAndFilter,
    updateProduct,
    fetchCategoriesAndSubcategories,
    toggleProductStat,
    deleteProducts // 1. Import the new deleteProducts action
} from "../../redux/slices/productsSlice";
import Sidebar from "../../components/common/AdminSideBar";
import Header from "../../components/common/AdminHeader";
import AdminLayout from "../../components/common/AdminLayout";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from "../../components/common/LoadingFallback";
import ErrorFallback from "../../components/common/ErrorFallback";

// ADD PRODUCT MODAL (This component remains unchanged)
function AddProductModal({ onClose }) {
    const [formData, setFormData] = useState({
        product_name: "",
        price: "",
        stock_level: "High",
        status: "ACTIVE",
        category: "",
        sub_category: ""
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    function handleSave() {
        onClose();
    }

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white w-[600px] max-w-full rounded-md p-6 relative">
                <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">
                    <IconX size={20} />
                </button>
                <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">Add New Product</h2>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold mb-1">Product Name</label>
                        <input
                            type="text"
                            name="product_name"
                            placeholder="Enter product name"
                            value={formData.product_name}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Price</label>
                        <input
                            type="text"
                            name="price"
                            placeholder="Enter price"
                            value={formData.price}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Stock Level</label>
                        <select
                            name="stock_level"
                            value={formData.stock_level}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        >
                            <option>High</option>
                            <option>Medium</option>
                            <option>Low</option>
                            <option>Out of Stock</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        >
                            <option>ACTIVE</option>
                            <option>INACTIVE</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Category</label>
                        <input
                            type="text"
                            name="category"
                            placeholder="Enter category"
                            value={formData.category}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Sub Category</label>
                        <input
                            type="text"
                            name="sub_category"
                            placeholder="Enter sub category"
                            value={formData.sub_category}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        />
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={handleSave}
                        className="bg-[#0B0C52] text-white px-4 py-2 rounded hover:opacity-90"
                    >
                        Save
                    </button>
                    <button
                        onClick={onClose}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

// EDIT PRODUCT MODAL (This component remains unchanged)
function EditProductModal({ product, onClose, onUpdate }) {
    const [formData, setFormData] = useState({
        product_name: product.product_name,
        price: product.price,
        stock_level: product.stock_level,
        status: product.status,
        category: product.category?.name || "",
        sub_category: product.sub_category?.name || ""
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdate = () => {
        onUpdate(formData);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white w-[600px] max-w-full rounded-md p-6 relative">
                <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">
                    <IconX size={20} />
                </button>
                <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">Edit Product</h2>
                <p className="text-sm text-gray-600 mb-4">
                    Editing product: <strong>{product.product_name}</strong>
                </p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold mb-1">Product Name</label>
                        <input
                            type="text"
                            name="product_name"
                            value={formData.product_name}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Price</label>
                        <input
                            type="text"
                            name="price"
                            value={formData.price}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Stock Level</label>
                        <select
                            name="stock_level"
                            value={formData.stock_level}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        >
                            <option>High</option>
                            <option>Medium</option>
                            <option>Low</option>
                            <option>Out of Stock</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Status</label>
                        <select
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        >
                            <option>ACTIVE</option>
                            <option>INACTIVE</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Category</label>
                        <input
                            type="text"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold mb-1">Sub Category</label>
                        <input
                            type="text"
                            name="sub_category"
                            value={formData.sub_category}
                            onChange={handleChange}
                            className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
                        />
                    </div>
                </div>

                <div className="flex gap-4">
                    <button
                        onClick={handleUpdate}
                        className="bg-[#0B0C52] text-white px-4 py-2 rounded hover:opacity-90"
                    >
                        Update
                    </button>
                    <button
                        onClick={onClose}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

// 2. REPLACE the old DeleteProductModal with this improved version
function DeleteProductModal({ items, onClose, onDelete, isLoading }) {
    const isBulkDelete = Array.isArray(items);
    const count = isBulkDelete ? items.length : 1;
    let message;

    if (isBulkDelete) {
        message = `Are you sure you want to permanently delete these ${count} selected products? This action cannot be undone.`;
    } else {
        message = `Are you sure you want to permanently delete "${items?.product_name}"? This action cannot be undone.`;
    }

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative">
                <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700" disabled={isLoading}>
                    <IconX size={20} />
                </button>
                <h2 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
                    <IconTrash size={20} />
                    Delete {count} Product(s)
                </h2>
                <p className="text-sm text-gray-600 mb-4">{message}</p>
                <div className="flex gap-4">
                    <button
                        onClick={onDelete}
                        className="bg-red-500 text-white px-4 py-2 rounded hover:opacity-90 disabled:bg-red-300"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                        onClick={onClose}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

// DEACTIVATE PRODUCT MODAL (This component remains unchanged)
function DeactivateProductModal({ product, onClose, onDeactivate }) {
    const actionText = product.status === 'ACTIVE' ? 'Deactivate' : 'Activate';
    const actionColor = product.status === 'ACTIVE' ? 'yellow' : 'blue';

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative">
                <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">
                    <IconX size={20} />
                </button>
                <h2 className={`text-lg font-semibold text-${actionColor}-600 mb-4 flex items-center gap-2`}>
                    <IconBan size={20} />
                    {actionText} Product
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                    Are you sure you want to {actionText.toLowerCase()} <strong>{product.product_name}</strong>?<br />
                    {product.status === 'ACTIVE'
                        ? "This product will no longer be available for purchase."
                        : "This product will be available for purchase."}
                </p>
                <div className="flex gap-4">
                    <button
                        onClick={onDeactivate}
                        className={`bg-${actionColor}-600 text-white px-4 py-2 rounded hover:opacity-90`}
                    >
                        Confirm
                    </button>
                    <button
                        onClick={onClose}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}


// FILTER MODAL (This component remains unchanged)
function FilterModal({
    isOpen,
    onClose,
    filters,
    setFilters,
    onApply,
    categories,
    subcategories
}) {
    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'category' ? { subCategory: "" } : {}) // Reset subcategory when category changes
        }));
    };

    const handleReset = () => {
        setFilters({
            category: "",
            subCategory: "",
            priceMin: "",
            priceMax: "",
            stockLevel: "",
            status: ""
        });
        onClose();
    };

    const selectedCategory = categories.find(cat => cat.category_name === filters.category);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white w-[400px] max-w-full rounded-md p-6 relative">
                <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">
                    <IconX size={20} />
                </button>
                <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">Filter Options</h2>

                <div className="mb-4">
                    <label className="block text-sm font-semibold mb-1">Category</label>
                    <select
                        name="category"
                        value={filters.category}
                        onChange={handleChange}
                        className="border border-gray-300 rounded px-3 py-2 w-full"
                    >
                        <option value="">All Categories</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.category_name}>
                                {category.category_name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-semibold mb-1">Sub Category</label>
                    <select
                        name="subCategory"
                        value={filters.subCategory}
                        onChange={handleChange}
                        className="border border-gray-300 rounded px-3 py-2 w-full"
                        disabled={!filters.category}
                    >
                        <option value="">All Subcategories</option>
                        {subcategories
                            .filter(sub => selectedCategory && sub.category === selectedCategory.id)
                            .map(subcategory => (
                                <option key={subcategory.id} value={subcategory.sub_category_name}>
                                    {subcategory.sub_category_name}
                                </option>
                            ))}
                    </select>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-semibold mb-1">Min Price</label>
                    <input
                        type="number"
                        name="priceMin"
                        value={filters.priceMin}
                        onChange={handleChange}
                        className="border border-gray-300 rounded px-3 py-2 w-full"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-semibold mb-1">Max Price</label>
                    <input
                        type="number"
                        name="priceMax"
                        value={filters.priceMax}
                        onChange={handleChange}
                        className="border border-gray-300 rounded px-3 py-2 w-full"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-semibold mb-1">Stock Level</label>
                    <select
                        name="stockLevel"
                        value={filters.stockLevel}
                        onChange={handleChange}
                        className="border border-gray-300 rounded px-3 py-2 w-full"
                    >
                        <option value="">All</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                        <option value="Out of Stock">Out of Stock</option>
                    </select>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-semibold mb-1">Status</label>
                    <select
                        name="status"
                        value={filters.status}
                        onChange={handleChange}
                        className="border border-gray-300 rounded px-3 py-2 w-full"
                    >
                        <option value="">All</option>
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                    </select>
                </div>

                <div className="flex justify-end gap-4">
                    <button
                        onClick={handleReset}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
                    >
                        Reset
                    </button>
                    <button
                        onClick={onClose}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            onApply();
                            onClose();
                        }}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                        Apply Filters
                    </button>
                </div>
            </div>
        </div>
    );
}

// ICON BUTTON COMPONENT (This component remains unchanged)
function IconButton({ children, onClick }) {
    return (
        <button onClick={onClick} className="p-1 hover:bg-gray-100 rounded transition">
            {children}
        </button>
    );
}

// MAIN COMPONENT
function ProductManagement() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {
        loading,
        error,
        data: allProducts = [],
        isSearching,
        categories = [],
        subcategories = [],
        categoriesLoading,
        deleteLoading // 3. Get the delete loading state from the selector
    } = useSelector((state) => state.products);

    // Local state
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [modalType, setModalType] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const { setLayoutConfig } = useAdminLayout();
    const [filters, setFilters] = useState({
        category: "",
        subCategory: "",
        priceMin: "",
        priceMax: "",
        stockLevel: "",
        status: ""
    });

    const pageSize = 10;

    useEffect(() => {
        setLayoutConfig({
            title: "Product Management",
            showBackButton: false,
            scrollable: false,
        });
        dispatch(fetchCategoriesAndSubcategories());
    }, [dispatch, setLayoutConfig]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchTerm.trim() !== '' ||
                filters.category ||
                filters.subCategory ||
                filters.priceMin ||
                filters.priceMax ||
                filters.stockLevel ||
                filters.status) {
                dispatch(searchAndFilter({
                    searchTerm,
                    ...filters
                }));
            } else {
                dispatch(fetchProductsList());
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm, filters, dispatch]);

    const filteredProducts = allProducts.filter((product) => {
        const matchesSearch = searchTerm.trim() === '' ||
            product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.category?.name?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilters = (
            (!filters.category || product.category?.name === filters.category) &&
            (!filters.subCategory || product.sub_category?.name === filters.subCategory) &&
            (!filters.priceMin || parseFloat(product.price) >= parseFloat(filters.priceMin)) &&
            (!filters.priceMax || parseFloat(product.price) <= parseFloat(filters.priceMax)) &&
            (!filters.stockLevel || product.stock_level === filters.stockLevel) &&
            (!filters.status || product.status === filters.status)
        );

        return matchesSearch && matchesFilters;
    });

    const totalCount = filteredProducts.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentProducts = filteredProducts.slice(startIndex, endIndex);
    const fromCount = startIndex + 1;
    const toCount = endIndex > totalCount ? totalCount : endIndex;

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handleRefresh = () => {
        setSearchTerm("");
        setFilters({
            category: "",
            subCategory: "",
            priceMin: "",
            priceMax: "",
            stockLevel: "",
            status: ""
        });
        setCurrentPage(1);
    };

    function openModal(type, product = null) {
        setSelectedProduct(product);
        setModalType(type);
    }

    function closeModal() {
        setSelectedProduct(null);
        setModalType(null);
    }

    const handleUpdateProduct = async (updatedData) => {
        try {
            await dispatch(updateProduct({
                productId: selectedProduct.id,
                productData: updatedData
            })).unwrap();
            closeModal();
        } catch (error) {
            console.error('Failed to update product:', error);
        }
    };

    // 4. REPLACE the placeholder handleDeleteProduct with this implementation
    const handleDeleteProduct = async () => {
        const idsToDelete = Array.isArray(selectedProduct)
            ? selectedProduct
            : [selectedProduct.id];

        if (!idsToDelete || idsToDelete.length === 0) return;

        try {
            await dispatch(deleteProducts(idsToDelete)).unwrap();
            closeModal();
            setSelectedProducts([]);
        } catch (error) {
            console.error('Failed to delete product(s):', error);
        }
    };

    const handleDeactivateProduct = async () => {
        try {
            await dispatch(toggleProductStat(selectedProduct.id)).unwrap();
            closeModal();
        } catch (error) {
            console.error('Failed to toggle product status:', error);
        }
    };

    const handleApplyFilters = () => {
        setCurrentPage(1);
        dispatch(searchAndFilter({
            searchTerm,
            ...filters
        }));
    };

    const toggleProductSelection = (productId) => {
        setSelectedProducts(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const selectAllProducts = () => {
        if (selectedProducts.length === currentProducts.length) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(currentProducts.map(product => product.id));
        }
    };

    if (loading && !isSearching) {
        return <LoadingFallback />
    }

    if (error) {
        return <ErrorFallback error={error} onRetry={() => dispatch(fetchProductsList())} />;
    }

    return (

        <div>
            <main className=" flex-1 bg-[#FFFFFF] text-gray-800 p-4 rounded">
                <div className=" rounded-md bg-[#FFFFFF] p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        {selectedProducts.length > 0 ? (
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-4 w-full">
                                <div className="flex items-center gap-4">
                                    <span className="text-sm text-gray-700 whitespace-nowrap">
                                        {selectedProducts.length} selected
                                    </span>
                                    <div className="relative ml-1">
                                        <input
                                            type="text"
                                            placeholder="Search By Order ID Or Customer Name"
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className=" border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 focus:outline-none focus:border-[3px] peer"
                                        />
                                        <IconSearch size={16} className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]" />
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {/* 5. Wire the bulk delete button's onClick */}
                                    <button
                                        className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm px-3 py-1 rounded hover:bg-red-50"
                                        onClick={() => openModal("DELETE", selectedProducts)}
                                    >
                                        <IconTrash size={16} />
                                        Delete
                                    </button>
                                    <button
                                        className="text-gray-500 hover:text-gray-700 text-sm px-3 py-1 rounded hover:bg-gray-100"
                                        onClick={() => setSelectedProducts([])}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="relative ml-1">
                                    <input
                                        type="text"
                                        placeholder="Search By Order ID Or Customer Name"
                                        value={searchTerm}
                                        onChange={(e) => {
                                            setSearchTerm(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                        className=" border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 focus:outline-none focus:border-[3px] peer"
                                    />
                                    <IconSearch size={16} className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]" />
                                </div>
                                <span className="text-sm text-gray-700 ml-auto mr-1">
                                    {fromCount} - {toCount} of {totalCount}
                                </span>
                                <button onClick={handlePrevPage} className="p-1 hover:bg-gray-100 rounded">
                                    <IconChevronUp size={18} />
                                </button>
                                <button onClick={handleNextPage} className="p-1 hover:bg-gray-100 rounded">
                                    <IconChevronDown size={18} />
                                </button>
                                <IconButton onClick={() => setIsFilterModalOpen(true)}>
                                    <IconFilter size={18} />
                                </IconButton>
                                <IconButton onClick={handleRefresh}>
                                    <IconRefresh size={18} />
                                </IconButton>
                            </>
                        )}
                    </div>

                    <div className="overflow-x-auto h-[calc(100vh-280px)]">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-[#F9F9FF] z-20 shadow-md">
                                <tr className="bg-[#F9F9FF] text-[#120E43]">
                                    <th className="px-4 py-2 w-10">
                                        <input
                                            type="checkbox"
                                            checked={selectedProducts.length > 0 && selectedProducts.length === currentProducts.length}
                                            onChange={selectAllProducts}
                                        />
                                    </th>
                                    <th className="px-4 py-2 text-left">Image</th>
                                    <th className="px-4 py-2 text-left">Product Name</th>
                                    <th className="px-4 py-2 text-left">Category</th>
                                    <th className="px-4 py-2 text-left">Sub Category</th>
                                    <th className="px-4 py-2 text-left">Price</th>
                                    <th className="px-4 py-2 text-left">Stock</th>
                                    <th className="px-4 py-2 text-left">Status</th>
                                    <th className="px-4 py-2 text-left">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map((product) => (
                                    <tr
                                        key={product.id}
                                        className="border-b border-gray-200 hover:bg-gray-50"
                                    >
                                        <td className="px-4 py-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedProducts.includes(product.id)}
                                                onChange={() => toggleProductSelection(product.id)}
                                            />
                                        </td>
                                        <td className="px-4 py-2">
                                            {product.template?.media_files?.[0]?.front_image ? (
                                                <img
                                                    src={product.template.media_files[0].front_image}
                                                    alt={product.product_name}
                                                    className="w-10 h-10 object-cover rounded"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 bg-gray-200 rounded"></div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">{product.product_name}</td>
                                        <td className="px-4 py-2">{product.category?.name || '-'}</td>
                                        <td className="px-4 py-2">{product.sub_category?.name || '-'}</td>
                                        <td className="px-4 py-2">${product.price}</td>
                                        <td className="px-4 py-2">
                                            <span className={`px-2 py-1 rounded-full text-xs ${product.stock_level === 'High' ? 'bg-green-100 text-green-800' :
                                                product.stock_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                                    product.stock_level === 'Low' ? 'bg-orange-100 text-orange-800' :
                                                        'bg-red-100 text-red-800'
                                                }`}>
                                                {product.stock_level} ({product.stock})
                                            </span>
                                        </td>
                                        <td className="px-4 py-2">
                                            {product.status === 'ACTIVE' ? (
                                                <span className="flex items-center gap-1 text-green-600">
                                                    <span role="img" aria-label="check">✅</span> Active
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1 text-red-600">
                                                    <span role="img" aria-label="cross">❌</span> Inactive
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                                    onClick={() => navigate(`/admin/products/${product.id}`)}
                                                >
                                                    <IconPencil size={16} />
                                                </button>
                                                <button
                                                    className={`${product.status === 'ACTIVE'
                                                        ? 'text-yellow-600 hover:text-yellow-800'
                                                        : 'text-green-600 hover:text-green-800'} flex items-center gap-1`}
                                                    onClick={() => openModal("DEACTIVATE", product)}
                                                >
                                                    {product.status === 'ACTIVE' ? (
                                                        <IconBan size={16} />
                                                    ) : (
                                                        <span className="text-green-600">✓</span>
                                                    )}
                                                </button>
                                                <button
                                                    className="text-red-500 hover:text-red-700 flex items-center gap-1"
                                                    onClick={() => openModal("DELETE", product)}
                                                >
                                                    <IconTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {currentProducts.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4 text-gray-500">
                                            {isSearching ? 'No products match your search criteria' : 'No products found'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-4 flex items-center justify-between bg-white border-t border-gray-200 p-4 rounded-md shadow-sm">
                        <div className="flex items-center gap-4 text-sm text-gray-700">
                            <span className="flex items-center gap-1">
                                <span role="img" aria-label="products">📦</span>
                                Total Products: <strong>{filteredProducts.length}</strong>
                            </span>
                            <span className="flex items-center gap-1 text-green-600">
                                <span role="img" aria-label="check">✅</span>
                                Active: <strong>{filteredProducts.filter(p => p.status === 'ACTIVE').length}</strong>
                            </span>
                            <span className="flex items-center gap-1 text-yellow-600">
                                <span role="img" aria-label="warning">⚠️</span>
                                Low Stock: <strong>{filteredProducts.filter(p => p.stock_level === 'Low').length}</strong>
                            </span>
                            <span className="flex items-center gap-1 text-red-600">
                                <span role="img" aria-label="warning">⛔</span>
                                Out of Stock: <strong>{filteredProducts.filter(p => p.stock_level === 'Out of Stock').length}</strong>
                            </span>
                        </div>
                    </div>
                </div>
            </main>

            {/* MODALS */}
            {modalType === "ADD" && <AddProductModal onClose={closeModal} />}
            {modalType === "EDIT" && selectedProduct && (
                <EditProductModal product={selectedProduct} onClose={closeModal} onUpdate={handleUpdateProduct} />
            )}
            {modalType === "DEACTIVATE" && selectedProduct && (
                <DeactivateProductModal product={selectedProduct} onClose={closeModal} onDeactivate={handleDeactivateProduct} />
            )}
            {/* 6. UPDATE the DeleteProductModal invocation */}
            {modalType === "DELETE" && selectedProduct && (
                <DeleteProductModal
                    items={selectedProduct}
                    onClose={closeModal}
                    onDelete={handleDeleteProduct}
                    isLoading={deleteLoading}
                />
            )}
            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                filters={filters}
                setFilters={setFilters}
                onApply={handleApplyFilters}
                categories={categories}
                subcategories={subcategories}
            />
        </div>
    );
}

export default ProductManagement;